import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { priceService } from '../../services/priceService';
import { dietService } from '../../services/dietService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import PriceModal from '../../components/Setup/PriceModal';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, PencilSquareIcon, TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Price } from '../../types/diet';
import { ColumnDef } from '@tanstack/react-table';
import DataGrid from '../../components/DataGrid';

const PricesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<Price | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Price | null>(null);
  const pageSize = 50;
  const queryClient = useQueryClient();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ name?: string; status?: string }>({});
  const navigate = useNavigate();


  const { data, isLoading } = useQuery({
    queryKey: ['prices', currentPage, searchTerm, sortBy, sortOrder, filters],
    queryFn: () => priceService.getPrices({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      sortBy,
      sortOrder,
      name: filters.name || undefined,
      status: filters.status || undefined,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dietService.deletePrice(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['prices'] }); setConfirmDelete(null); },
  });

  const handleSort = useCallback((column: string) => {
    if (sortBy === column) setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }, [sortBy]);

  const handleExport = useCallback(async () => {
    try {
      const blob = await priceService.exportPrices({
        search: searchTerm,
        ids: selectedIds.length ? selectedIds : undefined,
        name: filters.name,
        status: filters.status,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.style.display = 'none'; a.href = url; a.download = `prices-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed', e); }
  }, [filters.name, filters.status, searchTerm, selectedIds]);



  const toggleRow = useCallback((id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const handleBulkAction = useCallback(async (action: 'activate' | 'deactivate') => {
    try {
      const status = action === 'activate' ? 'ACTIVE' : 'INACTIVE';
      if (!selectedIds.length) return;
      await priceService.bulkUpdatePriceStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['prices'] });
    } catch (e) {
      console.error('Bulk update failed', e);
    }
  }, [queryClient, selectedIds]);


  const openCreate = useCallback(() => { setSelected(null); setIsModalOpen(true); }, []);
  const openEdit = useCallback((p: Price) => { setSelected(p); setIsModalOpen(true); }, []);


  const clearFilters = useCallback(() => { setFilters({}); setSearchTerm(''); setCurrentPage(1); }, []);

  const columns = useMemo<ColumnDef<Price>[]>(() => [
    { id: 'name', accessorKey: 'name', header: 'Name', meta: { sortable: true }, cell: (info) => info.getValue() as string },
    { id: 'description', header: 'Description', cell: ({ row }) => (row.original as Price).description || '-' },
    { id: 'type', header: 'Type', cell: ({ row }) => (row.original as any).type || '-' },
    { id: 'amount', accessorKey: 'amount', header: 'Amount', meta: { sortable: true }, cell: ({ row }) => (row.original as Price).amount },
    { id: 'currency', header: 'Currency', cell: ({ row }) => (row.original as Price).currency },
    { id: 'status', header: 'Status', cell: ({ row }) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${((row.original as Price).status === 'ACTIVE') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {(row.original as Price).status}
      </span>
    ) },
    { id: 'actions', header: 'Actions', cell: ({ row }) => (
      <div className="flex items-center justify-end space-x-2">
        <button onClick={() => navigate(`/setup/prices/${(row.original as Price).id}/manage`)} className="text-primary-600 hover:text-primary-900" title="Manage">
          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
        </button>
        <button onClick={() => openEdit(row.original as Price)} className="text-primary-600 hover:text-primary-900" title="Edit"><PencilSquareIcon className="w-4 h-4" /></button>
        <button onClick={() => setConfirmDelete(row.original as Price)} className="text-red-600 hover:text-red-900" title="Delete"><TrashIcon className="w-4 h-4" /></button>
      </div>
    ) },
  ], [openEdit, navigate]);

  const prices = data?.prices || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Prices</h1>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button variant="secondary" disabled={selectedIds.length === 0} onClick={() => handleBulkAction('activate')}>Activate</Button>
          <Button variant="secondary" disabled={selectedIds.length === 0} onClick={() => handleBulkAction('deactivate')}>Deactivate</Button>
          <Button variant="outline" disabled={selectedIds.length !== 1} onClick={() => navigate(`/setup/prices/${selectedIds[0]}/manage`)}>Manage</Button>
          <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>


      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input type="text" placeholder="Search prices..." className="form-input w-full" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-lg shadow-soft">
        <div className="p-4">
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                type="text"
                className="form-input w-full"
                placeholder="Filter name..."
                value={filters.name || ''}
                onChange={(e) => { setFilters((f) => ({ ...f, name: e.target.value || undefined })); setCurrentPage(1); }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-select w-full"
                value={filters.status || ''}
                onChange={(e) => { const v = e.target.value || undefined; setFilters((f) => ({ ...f, status: v })); setCurrentPage(1); }}
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : (
            <DataGrid
              data={prices}
              columns={columns}
              getRowId={(r) => (r as Price).id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) => setSelectedIds(checked ? ids : [])}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSort}
            />
          )}
        </div>
      </div>

        {/* Pagination */}
        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
            </p>
            <div>
              <Button variant="secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} className="rounded-r-none">Previous</Button>
              <Button variant="secondary" disabled={currentPage === pagination.pages} onClick={() => setCurrentPage(currentPage + 1)} className="rounded-l-none">Next</Button>
            </div>
          </div>
        )}

      {isModalOpen && (
        <PriceModal
          price={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['prices'] }); }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Price"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default PricesPage;
